import classNames from "classnames";
import { useCallback } from "react";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import { motion } from "framer-motion";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import Image from "next/image";
//styles
import styles from "./email.module.scss";
//component
import Input from "@/components/common/input/input";
import IconButton from "@/components/common/iconButton/iconButton";
import PrimaryButton from "@/components/common/primaryButton";
//types
import { EmailPropType } from "./type";
import { LoginStep } from "../../types";
import { AnimationType } from "../../loginmodal";
import { ButtonType } from "@/components/common/primaryButton/types";
//apis
import { ModifyUser, ModifyUserBody } from "@/apis/modifyUser/modifyUser";
//utils
import {
  mixpanelTrack,
  setSuperMixpanelProperty,
} from "@/helper/mixpanelService";
//zustans
import { useLoginStore } from "@/providers/login/login.provider";

const schema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type FormFields = z.infer<typeof schema>;

const Email = ({}: EmailPropType) => {
  const isKeyboardOpen = useDetectKeyboardOpen();

  const setState = useLoginStore((state) => state.setState);
  const token = useLoginStore((state) => state.token);
  const id = useLoginStore((state) => state.id);
  const phone = useLoginStore((state) => state.phone);
  const name = useLoginStore((state) => state.name);
  const animation = useLoginStore((state) => state.animationType);
  const showBackButton = useLoginStore((state) => state.showBackButton);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
  } = useForm<FormFields>({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(schema),
    // mode: "onChange",
  });

  const onHandle: SubmitHandler<FormFields> = useCallback(
    async (data) => {
      try {
        const body: ModifyUserBody = {
          email: data?.email?.trim(),
        };
        if (id) {
          const response = await ModifyUser(token, id, body);
          setSuperMixpanelProperty(response?.phone, "email", response?.email);
        }
        mixpanelTrack("Signup - Email Entered", {
          phone,
          name,
          email: data?.email?.trim(),
        });
        setState({
          animationType: AnimationType.SLIDEINRIGHT,
          step: LoginStep.HANDLE,
          email: data?.email?.trim(),
        });
        //   next && next(email);
      } catch (e) {
        console.error("error ModifyUser", e);
      }
    },
    [phone, name]
  );

  const handleBack = useCallback(() => {
    setState({
      animationType: AnimationType.SLIDEINLEFT,
      step: LoginStep.NAME,
    });
  }, []);

  return (
    <motion.form
      className={styles["container"]}
      initial={{
        x: animation === AnimationType.SLIDEINRIGHT ? "100%" : "-100%",
      }}
      animate={{ x: "0%" }}
      transition={{ type: "spring", damping: 20, stiffness: 100 }}
      exit={{ x: "-100%" }}
      onSubmit={handleSubmit(onHandle)}
    >
      <div className={styles["header"]}>
        <>
          {showBackButton && (
            <Image
              onClick={handleBack}
              height={22}
              width={22}
              src={"/icons/arrow-left.svg"}
              alt={"close-icon"}
            />
          )}
        </>
        <p>Create your Turf ID</p>
        <div />
      </div>

      <div className={classNames(styles["title-cont"])}>
        <p>What's your email?</p>
        <span>This will help us recover your account</span>
        <span>when you can't login</span>
      </div>
      <div className={classNames(styles["ph-24"], styles["relative"])}>
        <Input
          maxLength={30}
          placeholder="Email ID"
          register={register("email")}
        />
        <div className={classNames(styles["mt-16"], styles["relative"])}>
          <p className={classNames(styles.errorText)}>
            {errors?.email?.message || ""}
          </p>
        </div>
      </div>

      <div
        className={classNames(
          styles["button_container"],
          isKeyboardOpen && styles["bottom-45"]
        )}
      >
        <p className={styles["terms_text"]}>
          Don't sweat. We wont send you anything unless it is absolutely
          necessary
        </p>
        <div className={styles["circular-button"]}>
          <IconButton
            loading={isSubmitting}
            disabled={!isValid || isSubmitting}
            type="submit"
          />
        </div>
        <div className={styles["full-button"]}>
          <PrimaryButton
            buttonType={
              !isValid || isSubmitting
                ? ButtonType.DISABLED
                : ButtonType.PRIMARY
            }
            text={"Continue"}
            type="submit"
          />
        </div>
      </div>
    </motion.form>
  );
};

export default Email;
